import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useToast } from '../../components/ToastProvider';
import { getTelemetryEvents, type TelemetryEvent, type TelemetryEventsResponse } from '../../lib/flow-validator-api';
import { InstantTime } from '../../lib/time-format';
import { telemetryStats, botBand } from '../../lib/telemetry-stats';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, consoleZoneLabel, ZONES, type ZoneResult } from '../../lib/zones';
import { ALL_ZONES, matchesZoneFilter, countInZone, zoneBoardLegend, type Zoned } from '../../lib/zone-board';
import { ZoneCell, ZoneFanoutWarning, ZoneFilter } from '../../components/ZoneIndicators';
import { ZoneBoardLegend } from '../../components/ZoneBoardLegend';
import { useZoneReadReport } from '../../components/ZoneContext';
import { friendlyFromUnknown } from '../../lib/friendly-error';

/**
 * /admin/snippet-telemetry — ADR-071 A3 snippet telemetry surface.
 *
 * The snippet source's dedicated surface: ingest health (volume, bot flood,
 * live/synthetic/beta mix) plus RAW-EVENT DRILL-IN — because troubleshooting
 * snippet issues *is* reading the actual beacon payloads (A3 reverses the earlier
 * "raw-log viewer out of scope" framing). Reads the existing snippet telemetry
 * plane via GET /admin/diags/events (edge-ingest → Tinybird); modifies nothing
 * (A8 scope discipline). The dashboard widget's "Snippet" row links here.
 *
 * Distinct from the Flow Validator (/admin/diags), which pairs events with
 * report-cache for end-to-end pipeline verification. This view is snippet-ingest
 * operational health + payload inspection.
 *
 * ⭐ ZONE-AGNOSTIC READ-ONLY BOARD (admin-portal-ui-standards §6, 13 October 2026). It used to be
 * ADR-122 Pattern 3 (one zone at a time, chosen by a selector). It is a read-only board, so it now
 * shows BOTH zones by default: a Zone column, a per-zone legend over the unfiltered rows, and a
 * filter that defaults to "All zones". The read fans out from this browser, one request per zone
 * (ADR-120 Amendment 2) — never a server merge.
 *
 * ⛔ NEVER SUM TWO ZONES' FIGURES. Each zone's Tinybird workspace is a separate store (ADR-120
 * Stage 4). The summary cards are therefore one set PER ZONE in view, each labelled with its zone —
 * there is no "Events in window" that adds Australia's beacons to the United States'.
 */

const WINDOWS: { label: string; minutes: number }[] = [
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 6h', minutes: 360 },
  { label: 'Last 24h', minutes: 1440 },
];

const FETCH_CAP = 1000;

const LEGEND_COLUMNS = [
  { key: 'events', label: 'Events' },
  { key: 'realBots', label: 'Real bots' },
  { key: 'domains', label: 'Domains' },
] as const;

type ZonedEvent = Zoned<TelemetryEvent>;

function degradedOf(results: ReadonlyArray<ZoneResult<TelemetryEventsResponse>> | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of results ?? []) {
    const resp = r.items[0];
    if (r.error || !resp) continue;
    if (!resp.configured) out[r.zone.code] = 'Tinybird token not provisioned';
    else if (resp.error === 'query_budget_guard') out[r.zone.code] = 'Query budget guard — try later';
    else if (resp.error) out[r.zone.code] = 'Telemetry read degraded';
  }
  return out;
}

export function SnippetTelemetryPage() {
  const meState = useStaffMe();
  const role = meState.status === 'loaded' ? meState.me.role : undefined;
  // UAT fix: snippet telemetry is monitoring (telemetry:read = {nexa_admin, nexa_support, super_admin}).
  const canView = role === 'super_admin' || role === 'nexa_admin' || role === 'support';
  const toast = useToast();

  const [results, setResults] = useState<ZoneResult<TelemetryEventsResponse>[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(60);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  // §6 rule 4 — the filter narrows what is SHOWN; it defaults to "All zones" whatever the Working-in zone.
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);

  const load = useCallback(async (win: number) => {
    setLoading(true);
    setExpanded(null);
    try {
      const res = await fanOutZones(async (z) => [await getTelemetryEvents(win, FETCH_CAP, z)]);
      setResults(res); // kept even when every zone failed, so the legend names each "Did not respond"
      throwIfAllZonesFailed(res);
      const failed = failedZoneLabels(res);
      if (failed.length) {
        toast.push({ variant: 'error', title: 'Some telemetry could not be loaded', detail: `${failed.join(' and ')} did not respond — its beacon events are missing from this list.` });
      }
    } catch (err) {
      toast.push({ variant: 'error', title: 'Could not load snippet telemetry', detail: friendlyFromUnknown(err) });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (canView) void load(windowMinutes); }, [canView, windowMinutes, load]);

  // Every zone's events, each row tagged with its own zone (unfiltered — the legend counts these).
  const events: ZonedEvent[] = useMemo(() => {
    const out: ZonedEvent[] = [];
    for (const r of results ?? []) for (const ev of r.items[0]?.events ?? []) out.push({ ...ev, zone: r.zone.code });
    return out;
  }, [results]);
  const failedZones = results ? failedZoneLabels(results) : [];
  useZoneReadReport({ rows: results ? events : null, failedZones, filter: zoneFilter });

  const legend = zoneBoardLegend(results, {
    degraded: degradedOf(results),
    counts: (code) => {
      const st = telemetryStats(events.filter((e) => e.zone === code));
      return { events: countInZone(events, code), realBots: st.realBots, domains: st.domains };
    },
  });

  // The zones whose summary cards are on screen: the filtered zone, or every zone that answered.
  const zonesInView = (results ?? []).filter((r) => !r.error && (zoneFilter === ALL_ZONES || r.zone.code === zoneFilter));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = events
      .filter((e) => matchesZoneFilter(e, zoneFilter))
      .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp));
    if (!q) return list.slice(0, 200);
    return list
      .filter(
        (e) =>
          (e.hostname || '').toLowerCase().includes(q) ||
          e.domainToken.toLowerCase().includes(q) ||
          e.pageUrl.toLowerCase().includes(q) ||
          e.source.toLowerCase().includes(q),
      )
      .slice(0, 200);
  }, [events, search, zoneFilter]);
  const filteredCount = events.filter((e) => matchesZoneFilter(e, zoneFilter)).length;

  if (meState.status === 'loading') {
    return (
      <div className="alerts-page">
        <h1>Snippet Telemetry</h1>
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (!canView) {
    return (
      <div className="alerts-page">
        <h1>Snippet Telemetry</h1>
        <p className="portal-error" role="alert">
          Snippet telemetry is restricted to admin roles.
        </p>
      </div>
    );
  }

  const multiZone = ZONES.length > 1;

  return (
    <div className="alerts-page">
      <h1>Snippet Telemetry</h1>
      <div className="alerts-crumb">
        Admin → Snippet &nbsp;·&nbsp; ingest health + raw beacon drill-in (edge-ingest → Tinybird) · read-only · every zone
      </div>

      <ZoneBoardLegend
        rows={legend}
        columns={LEGEND_COLUMNS}
        testId="snippet-zone-legend"
        ariaLabel="Beacon events by zone"
        loading={loading && !results}
      />
      <ZoneFanoutWarning failedZones={failedZones} noun="beacon events" onRetry={() => void load(windowMinutes)} />

      {loading && !results && (
        <div className="alerts-empty" role="status" data-testid="telemetry-loading">Loading beacon events from every zone…</div>
      )}

      {zonesInView.map((r) => {
        const resp = r.items[0];
        const stats = telemetryStats(events.filter((e) => e.zone === r.zone.code));
        return (
          <section key={r.zone.code} className="snippet-zone-summary" data-zone={r.zone.code} aria-label={`Summary — ${consoleZoneLabel(r.zone.code)}`}>
            {multiZone && <div className="alerts-sectionlbl" data-testid="snippet-zone-heading">{consoleZoneLabel(r.zone.code)}</div>}
            {resp && !resp.configured && (
              <div className="error-banner">
                Tinybird admin token not provisioned in this zone — telemetry reads are unavailable (ops gap, not an error).
              </div>
            )}
            {resp?.error && resp.configured && <div className="error-banner">Telemetry read issue in this zone: {resp.error}</div>}
            <div className="alerts-sumbar">
              <div className="alerts-sumcard info">
                <div className="big">{stats.total}</div>
                <div>
                  <div className="lab">Events</div>
                  <div className="sub">in window</div>
                </div>
              </div>
              <div className={`alerts-sumcard ${botBand(stats.botPct, stats.real)}`} data-testid="bot-card">
                <div className="big">{stats.botPct}%</div>
                <div>
                  <div className="lab">Bot traffic · real only</div>
                  <div className="sub">{stats.realBots} of {stats.real} real flagged · {stats.synthetic} synthetic (ours) not counted</div>
                </div>
              </div>
              <div className="alerts-sumcard ok">
                <div className="big">{stats.domains}</div>
                <div>
                  <div className="lab">Domains</div>
                  <div className="sub">beaconing</div>
                </div>
              </div>
              <div className="alerts-sumcard info">
                <div className="big">{stats.live}</div>
                <div>
                  <div className="lab">Live</div>
                  <div className="sub">
                    {stats.synthetic} synthetic (ours) · {stats.beta} beta
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <div className="alerts-filters">
        <span className="alerts-filters-lab">Window:</span>
        {WINDOWS.map((w) => (
          <button
            key={w.minutes}
            type="button"
            className={`alerts-fpill${windowMinutes === w.minutes ? ' on' : ''}`}
            onClick={() => setWindowMinutes(w.minutes)}
          >
            {w.label}
          </button>
        ))}
        <div className="alerts-controls">
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="zone-board-filter" />
          <input
            className="alerts-search"
            type="search"
            placeholder="Search domain / page / source…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search events"
          />
        </div>
      </div>

      <div className="alerts-sectionlbl">Raw beacon events — newest first (click a row to inspect the payload)</div>

      {results && visible.length === 0 ? (
        <div className="alerts-empty" data-testid="telemetry-empty">
          {filteredCount === 0
            ? <EmptyWindow results={zonesInView} />
            : 'No beacon events match the search.'}
        </div>
      ) : results ? (
        <table className="alerts-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Time</th>
              <th style={{ width: 150 }}>Zone</th>
              <th>Domain</th>
              <th>Page</th>
              <th style={{ width: 90 }}>Device</th>
              <th style={{ width: 80 }}>Bot</th>
              <th style={{ width: 90 }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((e, i) => (
              <SnippetRow key={i} ev={e} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)} />
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** O2 — an empty window is not a broken pipeline: say when each zone in view last heard a beacon. */
function EmptyWindow({ results }: { results: ZoneResult<TelemetryEventsResponse>[] }) {
  const withLast = results.filter((r) => r.items[0]?.lastEventAt);
  if (withLast.length === 0) return <>No beacon events in this window.</>;
  if (results.length === 1) {
    return <>No beacon events in this window. Last event <InstantTime iso={withLast[0]!.items[0]!.lastEventAt!} mode="datetime" /> — widen the window.</>;
  }
  return (
    <>
      No beacon events in this window.{' '}
      {withLast.map((r, i) => (
        <span key={r.zone.code}>
          {i > 0 && ' · '}
          {consoleZoneLabel(r.zone.code)}: last event <InstantTime iso={r.items[0]!.lastEventAt!} mode="datetime" />
        </span>
      ))}
      {' '}— widen the window.
    </>
  );
}

function SnippetRow({ ev, expanded, onToggle }: { ev: ZonedEvent; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }} className={ev.isBot === 1 ? 'row-ack' : ''}>
        <td className="alerts-when"><InstantTime iso={ev.timestamp} mode="datetime" /></td>
        <td data-testid="snippet-zone-cell"><ZoneCell zone={ev.zone} /></td>
        <td className="alerts-subj">{ev.hostname || ev.domainToken || 'unknown'}</td>
        <td className="alerts-subj-sub">{ev.pageUrl}</td>
        <td>{ev.deviceType}</td>
        <td>{ev.isBot === 1 ? <span className="alerts-stat alerts-stat-flap">bot</span> : '—'}</td>
        <td>
          <span className={`alerts-src alerts-src-${ev.source === 'live' ? 'auth' : ev.source === 'synthetic' ? 'deploy' : 'scoring'}`}>
            {ev.source}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ paddingTop: 0 }}>
            <div className="alerts-drill">
              <h4>Raw beacon payload</h4>
              {Object.entries(ev).map(([k, v]) => (
                <div className="alerts-evt" key={k}>
                  <span className="t">{k}</span>
                  <span className="d">
                    <code>{String(v) === '' ? '—' : String(v)}</code>
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
